const translations = {
  'us-en': {
    locale: 'us-en',
    country: 'US',
    flag: '\u{1F1FA}\u{1F1F8}',
    countryName: 'United States',
    langName: 'English',
    currency: { symbol: '$', code: 'USD' },
    nav: { howItWorks: 'How it works', attorneys: 'Attorneys', engine: 'Engine', pricing: 'Pricing', faq: 'FAQ', signIn: 'Sign in', getStarted: 'Get started' },
    hero: {
      badge: 'Trusted by 12,000+ Americans',
      h1: ['Your lawyer,', 'without the', 'lawyer fees.'],
      sub: 'Got a legal problem? Upload your document. Archer analyzes your risk in a few minutes, tells you <strong>exactly what to do</strong>, and connects you to a real attorney only when you need one.',
      cta: 'Analyze my document · free',
      ctaLawyer: 'Talk to a lawyer now',
      footnote: 'No credit card · A few minutes · Licensed US attorneys on demand',
      riskLabel: 'Your Risk Score',
      riskAction: 'Act within 7 days',
      availableNow: '3 attorneys available now',
    },
    animated: {
      label: 'See Archer in action',
      h2: ['Whatever your situation,', 'Archer is already on it.'],
      sub: 'Every case tracked. Every deadline visible. Every risk scored instantly.',
      aiBar: 'Archer AI · <span class="text-[#1a56db] font-medium">78% of cases resolved by AI alone, no lawyer needed.</span>',
    },
    stats: {
      docs: '47,284', docsLabel: 'Documents analyzed',
      users: '12,431', usersLabel: 'Americans protected',
      saved: '$8.2M', savedLabel: 'Legal fees saved',
      rating: '4.9/5', ratingLabel: 'Average rating',
    },
    trust: ['Licensed US attorneys', 'Results in a few minutes', '256-bit encryption', 'Attorney-reviewed AI', 'No hidden fees'],
    press: 'As seen in',
    categories: {
      sectionLabel: 'What Archer handles',
      h2: 'Whatever your legal problem, Archer handles it.',
      sub: 'From demand letters to employment disputes, lease issues to business contracts, Archer covers every common legal situation Americans face every day.',
      items: [
        { title: 'Demand letters', desc: 'Received a threatening legal letter? Know your rights and how to respond.', badge: 'Most common', badgeClass: 'badge-blue' },
        { title: 'Employment disputes', desc: 'Wrongful termination, unpaid wages, discrimination, non-compete clauses.', badge: 'High risk', badgeClass: 'badge-red' },
        { title: 'Lease & tenant rights', desc: 'Illegal eviction, deposit disputes, unfair clauses, landlord violations.', badge: 'Very common', badgeClass: 'badge-green' },
        { title: 'Business contracts', desc: 'NDA, partnership agreements, service contracts, vendor disputes.', badge: 'B2B', badgeClass: 'badge-blue' },
        { title: 'Debt collection', desc: "Collectors harassing you? Know your FDCPA rights and how to stop them.", badge: 'Urgent', badgeClass: 'badge-red' },
        { title: 'Consumer rights', desc: 'Scams, refund disputes, warranty claims, unfair business practices.', badge: 'Common', badgeClass: 'badge-green' },
        { title: 'Immigration documents', desc: 'Visa contracts, employment authorization, sponsor agreements review.', badge: 'Expats', badgeClass: 'badge-blue' },
        { title: 'Court notices', desc: 'Summons, small claims, subpoenas: understand what you received.', badge: 'Time-sensitive', badgeClass: 'badge-red' },
        { title: 'Divorce & family law', desc: 'Separation agreements, custody basics, asset division overview.', badge: 'Sensitive', badgeClass: 'badge-gray' },
      ],
    },
    problem: {
      label: 'The problem',
      h2: 'Legal help used to cost $500 just to ask a question.',
      p: "You received a demand letter. A contract you don't understand. An eviction notice. You're stressed, and a real lawyer costs $500/hr just to pick up the phone. Archer changes that.",
      steps: ['Upload your document in seconds', 'Get your Risk Score instantly', 'Know exactly what to do next', 'Talk to a real lawyer for $149, not $500/hr'],
      mockFile: 'demand_letter.pdf uploaded',
      mockAnalyzing: 'Analysing…',
      mockScore: '78 / 100 · High Risk',
      mockAction: 'Respond within 10 days',
      mockFindings: ['Clause 3.2 legally enforceable', 'Financial exposure up to $8,400', 'Strong case for negotiation'],
    },
    howItWorks: {
      label: 'How it works',
      h2: 'From confused to confident, in 4 steps',
      steps: [
        { title: 'Upload your document', desc: 'Any format. Demand letter, contract, court notice. Takes 10 seconds.' },
        { title: 'Get your Risk Score', desc: 'Score from 0 to 100. Plain English. No jargon. In a few minutes.' },
        { title: 'Your case stays alive', desc: 'Add docs as they arrive. Score updates. Next step always clear.' },
        { title: 'Talk to a lawyer', desc: '$149 for 30 min. Attorney pre-briefed. No time wasted.' },
      ],
    },
    lawyersCta: {
      badge: 'Attorneys available right now',
      h2: ['Talk to a real lawyer.', 'By video. Right now.'],
      p: "Don't wait days for an appointment. Connect to a licensed US attorney in minutes, pre-briefed with your full case. 30 minutes, flat fee, no surprises.",
      cta: 'Book a video call · $149',
      footnote: 'Available same day · Any US state · Licensed & vetted',
      availableNow: 'Available now',
      availableIn: 'Available in',
    },
    lawyersSection: { label: 'Our attorneys', h2: 'Real lawyers. Vetted. Ready.' },
    pricing: {
      label: 'Pricing',
      h2: 'Lawyer-level help. Not lawyer-level prices.',
      free: { name: 'Free', price: '$0', period: 'forever', cta: 'Start for free', features: ['1 free document analysis', 'Risk Score overview'], disabled: ['Full risk report', 'Live case file', 'Attorney access'] },
      pro: { name: 'Pro', price: '$69', period: 'per month', cta: 'Get started', badge: 'Most popular', features: ['Unlimited analyses', 'Full risk reports', 'Live case file', 'Dynamic Risk Score', 'Priority support'] },
      attorney: { name: 'Attorney call', price: '$149', period: 'per session', cta: 'Book a call', features: ['30-min video call', 'Licensed US attorney', 'Pre-briefed by Archer AI', 'Written follow-up', 'Same-day availability'] },
    },
    reviews: {
      label: 'Reviews',
      h2: 'Real people. Real results.',
      items: [
        { text: '"I got a demand letter and panicked. Archer told me my risk was 78/100 and exactly what to do. The attorney call saved me from a huge mistake."', name: 'Michael R.', loc: 'New York, NY' },
        { text: '"The attorney already knew my entire case before we spoke. Best $149 I ever spent. Would have paid $2,000 at a regular firm."', name: 'Jennifer L.', loc: 'Austin, TX' },
        { text: '"My landlord sent an illegal eviction notice. Archer caught it instantly. My score dropped from 71 to 14 after I sent the response it helped me draft."', name: 'Carlos M.', loc: 'Miami, FL' },
      ],
    },
    faq: {
      label: 'FAQ',
      h2: 'Questions you probably have',
      items: [
        { q: 'Is Archer giving me real legal advice?', a: 'Archer provides legal information and document analysis, not legal advice. When you need actual legal advice, Archer connects you to a licensed attorney.' },
        { q: 'Is my document secure?', a: 'All documents are encrypted with 256-bit encryption in transit and at rest. We never share your documents with third parties. You can delete your data anytime.' },
        { q: 'Who are the attorneys on Archer?', a: 'All attorneys are independently licensed in their US states, with a minimum of 5 years of experience, and go through a rigorous vetting process before joining Archer.' },
        { q: 'What if I need a lawyer in court?', a: 'Archer is designed for advice and document analysis, not court representation. Our attorneys can advise you and refer you to the right specialist if needed.' },
        { q: 'How accurate is the Risk Score?', a: 'The Risk Score is generated by AI trained on thousands of legal documents and validated by licensed attorneys. It is a guidance tool to help you understand urgency and complexity.' },
        { q: 'Can I cancel my subscription anytime?', a: 'Yes. Cancel anytime with one click. No questions asked, no cancellation fees. Your case files remain accessible until the end of your billing period.' },
      ],
    },
    finalCta: {
      h2: ['Your lawyer, without', 'the lawyer fees.'],
      p: 'Join 12,000+ Americans who use Archer to protect themselves, for less.',
      cta: 'Analyze my document · it\'s free',
    },
    footer: 'Archer provides legal information and document analysis, not legal advice. Attorney consultations are conducted by independent, licensed legal professionals. Archer does not create an attorney-client relationship.',
    lawyers: [
      { name: 'Sarah Mitchell, Esq.', specialty: 'Employment Law', bar: 'New York Bar', years: 12, tags: ['Wrongful termination', 'Discrimination'], rating: 5.0, sessions: 214, status: 'now', photo: 'https://images.unsplash.com/photo-1585240975858-7264fd020798?w=200&h=200&fit=crop' },
      { name: 'Archer Carter, Esq.', specialty: 'Contract Law', bar: 'California Bar', years: 9, tags: ['Business disputes', 'NDA'], rating: 4.9, sessions: 187, status: '8 min', photo: 'https://images.unsplash.com/photo-1644268756851-3f69ffb9553f?w=200&h=200&fit=crop' },
      { name: 'Diana Torres, Esq.', specialty: 'Tenant Rights', bar: 'Texas Bar', years: 7, tags: ['Lease disputes', 'Eviction'], rating: 4.9, sessions: 156, status: '22 min', photo: 'https://images.unsplash.com/photo-1685760259914-ee8d2c92d2e0?w=200&h=200&fit=crop' },
    ],
  },

  'be-fr': {
    locale: 'be-fr',
    country: 'BE',
    flag: '\u{1F1E7}\u{1F1EA}',
    countryName: 'Belgium',
    langName: 'Fran\u00e7ais',
    currency: { symbol: '\u20ac', code: 'EUR' },
    nav: { howItWorks: 'Comment \u00e7a marche', attorneys: 'Avocats', engine: 'Moteur', pricing: 'Tarifs', faq: 'FAQ', signIn: 'Connexion', getStarted: 'Commencer' },
    hero: {
      badge: 'Utilis\u00e9 par 4 200+ Belges',
      h1: ['Votre avocat,', 'sans les', 'honoraires.'],
      sub: "Un probl\u00e8me juridique ? T\u00e9l\u00e9chargez votre document. Archer analyse votre risque en quelques minutes, vous dit <strong>exactement quoi faire</strong> \u2014 et vous connecte \u00e0 un vrai avocat uniquement quand c'est n\u00e9cessaire.",
      cta: 'Analyser mon document · gratuit',
      ctaLawyer: 'Parler \u00e0 un avocat maintenant',
      footnote: 'Sans carte bancaire \u00b7 quelques minutes \u00b7 Avocats belges agr\u00e9\u00e9s',
      riskLabel: 'Votre Score de Risque',
      riskAction: 'Agir sous 7 jours',
      availableNow: '3 avocats disponibles',
    },
    animated: {
      label: 'Archer en action',
      h2: ['Quelle que soit votre situation \u2014', 'Archer s\'en occupe d\u00e9j\u00e0.'],
      sub: 'Chaque dossier suivi. Chaque d\u00e9lai visible. Chaque risque \u00e9valu\u00e9 instantan\u00e9ment.',
      aiBar: 'Archer AI \u2014 <span class="text-[#1a56db] font-medium">78% des dossiers r\u00e9solus par l\'IA seule \u2014 sans avocat.</span>',
    },
    stats: {
      docs: '11 832', docsLabel: 'Documents analys\u00e9s',
      users: '4 217', usersLabel: 'Belges prot\u00e9g\u00e9s',
      saved: '\u20ac2,1M', savedLabel: 'Frais juridiques \u00e9conomis\u00e9s',
      rating: '4.9/5', ratingLabel: 'Note moyenne',
    },
    trust: ['Avocats belges agr\u00e9\u00e9s', 'R\u00e9sultats en quelques minutes', 'Chiffrement 256-bit', 'IA valid\u00e9e par des avocats', 'Aucun frais cach\u00e9'],
    press: 'Vu dans',
    categories: {
      sectionLabel: 'Ce que Archer g\u00e8re',
      h2: 'Quel que soit votre probl\u00e8me juridique \u2014 Archer le g\u00e8re.',
      sub: 'Des mises en demeure aux litiges de travail, des baux aux contrats commerciaux \u2014 Archer couvre toutes les situations juridiques courantes en Belgique.',
      items: [
        { title: 'Mises en demeure', desc: 'Re\u00e7u une lettre mena\u00e7ante ? Connaissez vos droits et comment r\u00e9pondre.', badge: 'Fr\u00e9quent', badgeClass: 'badge-blue' },
        { title: 'Droit du travail', desc: 'Licenciement abusif, salaires impay\u00e9s, discrimination, clause de non-concurrence.', badge: 'Haut risque', badgeClass: 'badge-red' },
        { title: 'Bail & droit locatif', desc: 'Expulsion ill\u00e9gale, litiges de garantie, clauses abusives, obligations du bailleur.', badge: 'Tr\u00e8s fr\u00e9quent', badgeClass: 'badge-green' },
        { title: 'Contrats commerciaux', desc: 'NDA, accords de partenariat, contrats de service, litiges fournisseurs.', badge: 'B2B', badgeClass: 'badge-blue' },
        { title: 'Recouvrement de cr\u00e9ances', desc: 'Harcel\u00e9 par un huissier ? Connaissez vos droits et comment stopper les proc\u00e9dures.', badge: 'Urgent', badgeClass: 'badge-red' },
        { title: 'Protection du consommateur', desc: 'Arnaques, litiges de remboursement, garanties, pratiques commerciales d\u00e9loyales.', badge: 'Courant', badgeClass: 'badge-green' },
        { title: 'Droit de la famille', desc: 'Divorce, garde des enfants, pension alimentaire, partage des biens.', badge: 'Sensible', badgeClass: 'badge-gray' },
        { title: 'Droit immobilier', desc: 'Compromis de vente, vices cach\u00e9s, copropri\u00e9t\u00e9, litiges de voisinage.', badge: 'Valeur \u00e9lev\u00e9e', badgeClass: 'badge-red' },
        { title: 'Droit administratif', desc: 'Amendes, permis d\'urbanisme, litiges avec l\'administration communale.', badge: 'Complexe', badgeClass: 'badge-blue' },
      ],
    },
    problem: {
      label: 'Le probl\u00e8me',
      h2: "L'aide juridique co\u00fbtait 300\u20ac rien que pour poser une question.",
      p: "Vous avez re\u00e7u une mise en demeure. Un contrat incompr\u00e9hensible. Un avis d'expulsion. Vous \u00eates stress\u00e9 \u2014 et un vrai avocat co\u00fbte 250\u20ac/h rien que pour d\u00e9crocher le t\u00e9l\u00e9phone. Archer change la donne.",
      steps: ['T\u00e9l\u00e9chargez votre document en secondes', 'Obtenez votre Score de Risque instantan\u00e9ment', 'Savez exactement quoi faire ensuite', 'Parlez \u00e0 un vrai avocat pour 139\u20ac \u2014 pas 250\u20ac/h'],
      mockFile: 'mise_en_demeure.pdf t\u00e9l\u00e9charg\u00e9',
      mockAnalyzing: 'Analyse en cours…',
      mockScore: '78 / 100 \u2014 Risque \u00e9lev\u00e9',
      mockAction: 'R\u00e9pondre sous 10 jours',
      mockFindings: ['Article 3.2 juridiquement contraignant', 'Exposition financi\u00e8re jusqu\'\u00e0 8 400\u20ac', 'Bonne opportunit\u00e9 de n\u00e9gociation'],
    },
    howItWorks: {
      label: 'Comment \u00e7a marche',
      h2: 'De confus \u00e0 confiant \u2014 en 4 \u00e9tapes',
      steps: [
        { title: 'T\u00e9l\u00e9chargez votre document', desc: 'Tout format. Mise en demeure, contrat, notification. 10 secondes.' },
        { title: 'Obtenez votre Score de Risque', desc: 'Score de 0\u2013100. En fran\u00e7ais. Sans jargon. En quelques minutes.' },
        { title: 'Votre dossier reste vivant', desc: 'Ajoutez des documents. Le score se met \u00e0 jour. La prochaine \u00e9tape est toujours claire.' },
        { title: 'Parlez \u00e0 un avocat', desc: '139\u20ac pour 30 min. Avocat d\u00e9j\u00e0 brief\u00e9. Z\u00e9ro temps perdu.' },
      ],
    },
    lawyersCta: {
      badge: 'Avocats disponibles maintenant',
      h2: ['Parlez \u00e0 un vrai avocat.', 'Par vid\u00e9o. Maintenant.'],
      p: "N'attendez pas des jours pour un rendez-vous. Connectez-vous \u00e0 un avocat belge agr\u00e9\u00e9 en quelques minutes \u2014 d\u00e9j\u00e0 brief\u00e9 sur votre dossier. 30 minutes, tarif fixe, sans surprise.",
      cta: 'R\u00e9server un appel vid\u00e9o \u2014 139\u20ac',
      footnote: 'Disponible le jour m\u00eame \u00b7 Toute la Belgique \u00b7 Avocats agr\u00e9\u00e9s',
      availableNow: 'Disponible',
      availableIn: 'Disponible dans',
    },
    lawyersSection: { label: 'Nos avocats', h2: 'Vrais avocats. V\u00e9rifi\u00e9s. Pr\u00eats.' },
    pricing: {
      label: 'Tarifs',
      h2: "L'aide d'un avocat. Pas le prix d'un avocat.",
      free: { name: 'Gratuit', price: '0\u20ac', period: 'pour toujours', cta: 'Commencer gratuitement', features: ['1 analyse de document gratuite', 'Aper\u00e7u du Score de Risque'], disabled: ['Rapport complet', 'Dossier en direct', 'Acc\u00e8s avocat'] },
      pro: { name: 'Pro', price: '65\u20ac', period: 'par mois', cta: 'Commencer', badge: 'Le plus populaire', features: ['Analyses illimit\u00e9es', 'Rapports complets', 'Dossier en direct', 'Score de Risque dynamique', 'Support prioritaire'] },
      attorney: { name: 'Appel avocat', price: '139\u20ac', period: 'par session', cta: 'R\u00e9server', features: ['Appel vid\u00e9o de 30 min', 'Avocat belge agr\u00e9\u00e9', 'Brief\u00e9 par Archer AI', 'Suivi \u00e9crit', 'Disponibilit\u00e9 le jour m\u00eame'] },
    },
    reviews: {
      label: 'T\u00e9moignages',
      h2: 'De vrais t\u00e9moignages. De vrais r\u00e9sultats.',
      items: [
        { text: '"J\'ai re\u00e7u une mise en demeure et j\'ai paniqu\u00e9. Archer m\'a dit que mon risque \u00e9tait de 78/100 et exactement quoi faire. L\'appel avec l\'avocat m\'a sauv\u00e9 d\'une grosse erreur."', name: 'Marc D.', loc: 'Bruxelles, Belgique' },
        { text: '"L\'avocat connaissait d\u00e9j\u00e0 tout mon dossier avant notre appel. Les meilleurs 139\u20ac que j\'ai d\u00e9pens\u00e9s. J\'aurais pay\u00e9 1 500\u20ac dans un cabinet classique."', name: 'Sophie V.', loc: 'Li\u00e8ge, Belgique' },
        { text: '"Mon propri\u00e9taire m\'a envoy\u00e9 un avis d\'expulsion ill\u00e9gal. Archer l\'a d\u00e9tect\u00e9 imm\u00e9diatement. Mon score est pass\u00e9 de 71 \u00e0 14 apr\u00e8s avoir envoy\u00e9 la r\u00e9ponse."', name: 'Thomas B.', loc: 'Namur, Belgique' },
      ],
    },
    faq: {
      label: 'FAQ',
      h2: 'Questions fr\u00e9quentes',
      items: [
        { q: 'Archer donne-t-il de vrais conseils juridiques ?', a: 'Archer fournit des informations juridiques et une analyse de documents \u2014 pas des conseils juridiques. Quand vous avez besoin de vrais conseils, Archer vous connecte \u00e0 un avocat agr\u00e9\u00e9.' },
        { q: 'Mon document est-il s\u00e9curis\u00e9 ?', a: 'Tous les documents sont chiffr\u00e9s avec un chiffrement 256-bit en transit et au repos. Nous ne partageons jamais vos documents. Vous pouvez supprimer vos donn\u00e9es \u00e0 tout moment.' },
        { q: 'Qui sont les avocats sur Archer ?', a: 'Tous les avocats sont inscrits au Barreau belge, avec un minimum de 5 ans d\'exp\u00e9rience, et passent par un processus de v\u00e9rification rigoureux.' },
        { q: 'Archer comprend-il le droit belge ?', a: 'Oui. Archer est form\u00e9 sur le Code civil belge, le Code judiciaire, le droit du travail belge, les r\u00e9glementations r\u00e9gionales (Wallonie, Flandre, Bruxelles) et la jurisprudence belge.' },
        { q: 'Quelle est la pr\u00e9cision du Score de Risque ?', a: "Le Score de Risque est g\u00e9n\u00e9r\u00e9 par une IA form\u00e9e sur des milliers de documents juridiques et valid\u00e9e par des avocats. C'est un outil d'orientation." },
        { q: 'Puis-je annuler mon abonnement \u00e0 tout moment ?', a: 'Oui. Annulez \u00e0 tout moment en un clic. Sans questions, sans frais d\'annulation. Vos dossiers restent accessibles jusqu\'\u00e0 la fin de votre p\u00e9riode de facturation.' },
      ],
    },
    finalCta: {
      h2: ['Votre avocat, sans', 'les honoraires.'],
      p: 'Rejoignez 4 200+ Belges qui utilisent Archer pour se prot\u00e9ger \u2014 pour moins cher.',
      cta: 'Analyser mon document \u2014 c\'est gratuit',
    },
    footer: "Archer fournit des informations juridiques et une analyse de documents, pas des conseils juridiques. Les consultations d'avocats sont r\u00e9alis\u00e9es par des professionnels ind\u00e9pendants agr\u00e9\u00e9s. Archer ne cr\u00e9e pas de relation avocat-client.",
    lawyers: [
      { name: 'Sophie Lecomte', specialty: 'Droit du travail', bar: 'Barreau de Bruxelles', years: 10, tags: ['Licenciement', 'Discrimination'], rating: 5.0, sessions: 167, status: 'now', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop' },
      { name: 'Thomas Dupont', specialty: 'Droit immobilier', bar: 'Barreau de Li\u00e8ge', years: 8, tags: ['Bail', 'Copropri\u00e9t\u00e9'], rating: 4.9, sessions: 134, status: '10 min', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop' },
      { name: 'Julie Renard', specialty: 'Droit de la famille', bar: 'Barreau de Namur', years: 12, tags: ['Divorce', 'Garde'], rating: 4.9, sessions: 189, status: '18 min', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop' },
    ],
  },

  'be-nl': {
    locale: 'be-nl',
    country: 'BE',
    flag: '\u{1F1E7}\u{1F1EA}',
    countryName: 'Belgium',
    langName: 'Nederlands',
    currency: { symbol: '\u20ac', code: 'EUR' },
    nav: { howItWorks: 'Hoe het werkt', attorneys: 'Advocaten', engine: 'Engine', pricing: 'Prijzen', faq: 'FAQ', signIn: 'Inloggen', getStarted: 'Beginnen' },
    hero: {
      badge: 'Vertrouwd door 4.200+ Belgen',
      h1: ['Uw advocaat,', 'zonder de', 'advocaatkosten.'],
      sub: 'Een juridisch probleem? Upload uw document. Archer analyseert uw risico in enkele minuten, vertelt u <strong>precies wat u moet doen</strong> \u2014 en verbindt u met een echte advocaat alleen wanneer dat nodig is.',
      cta: 'Analyseer mijn document \u2014 gratis',
      ctaLawyer: 'Spreek nu met een advocaat',
      footnote: 'Geen creditcard \u00b7 enkele minuten \u00b7 Erkende Belgische advocaten',
      riskLabel: 'Uw Risicoscore',
      riskAction: 'Handel binnen 7 dagen',
      availableNow: '3 advocaten beschikbaar',
    },
    animated: {
      label: 'Archer in actie',
      h2: ['Wat uw situatie ook is \u2014', 'Archer is er al mee bezig.'],
      sub: 'Elk dossier gevolgd. Elke deadline zichtbaar. Elk risico onmiddellijk beoordeeld.',
      aiBar: 'Archer AI \u2014 <span class="text-[#1a56db] font-medium">78% van de dossiers opgelost door AI alleen \u2014 geen advocaat nodig.</span>',
    },
    stats: {
      docs: '11.832', docsLabel: 'Documenten geanalyseerd',
      users: '4.217', usersLabel: 'Belgen beschermd',
      saved: '\u20ac2,1M', savedLabel: 'Juridische kosten bespaard',
      rating: '4.9/5', ratingLabel: 'Gemiddelde beoordeling',
    },
    trust: ['Erkende Belgische advocaten', 'Resultaten in enkele minuten', '256-bit encryptie', 'Door advocaten gevalideerde AI', 'Geen verborgen kosten'],
    press: 'Bekend van',
    categories: {
      sectionLabel: 'Wat Archer behandelt',
      h2: 'Wat uw juridisch probleem ook is \u2014 Archer regelt het.',
      sub: 'Van ingebrekestellingen tot arbeidsgeschillen, van huurproblemen tot handelscontracten \u2014 Archer dekt alle veelvoorkomende juridische situaties in Belgi\u00eb.',
      items: [
        { title: 'Ingebrekestellingen', desc: 'Een dreigende juridische brief ontvangen? Ken uw rechten en hoe u moet reageren.', badge: 'Frequent', badgeClass: 'badge-blue' },
        { title: 'Arbeidsrecht', desc: 'Onrechtmatig ontslag, onbetaalde lonen, discriminatie, concurrentiebeding.', badge: 'Hoog risico', badgeClass: 'badge-red' },
        { title: 'Huur & huurdersrechten', desc: 'Illegale uitzetting, geschillen over waarborg, oneerlijke clausules.', badge: 'Zeer frequent', badgeClass: 'badge-green' },
        { title: 'Handelscontracten', desc: 'NDA, partnerschapsovereenkomsten, dienstencontracten, leveranciersgeschillen.', badge: 'B2B', badgeClass: 'badge-blue' },
        { title: 'Incasso', desc: 'Lastiggevallen door een deurwaarder? Ken uw rechten en stop de procedures.', badge: 'Dringend', badgeClass: 'badge-red' },
        { title: 'Consumentenbescherming', desc: 'Oplichting, terugbetalingsgeschillen, garantieclaims, oneerlijke handelspraktijken.', badge: 'Veel voorkomend', badgeClass: 'badge-green' },
        { title: 'Familierecht', desc: 'Echtscheiding, voogdij, alimentatie, verdeling van goederen.', badge: 'Gevoelig', badgeClass: 'badge-gray' },
        { title: 'Vastgoedrecht', desc: 'Compromis, verborgen gebreken, mede-eigendom, burengeschillen.', badge: 'Hoge waarde', badgeClass: 'badge-red' },
        { title: 'Bestuursrecht', desc: 'Boetes, stedenbouwkundige vergunningen, geschillen met de gemeente.', badge: 'Complex', badgeClass: 'badge-blue' },
      ],
    },
    problem: {
      label: 'Het probleem',
      h2: 'Juridische hulp kostte \u20ac300 alleen al om een vraag te stellen.',
      p: 'U heeft een ingebrekestelling ontvangen. Een onbegrijpelijk contract. Een uitzettingsbericht. U bent gestrest \u2014 en een echte advocaat kost \u20ac250/uur. Archer verandert dat.',
      steps: ['Upload uw document in seconden', 'Krijg uw Risicoscore onmiddellijk', 'Weet precies wat u moet doen', 'Spreek met een echte advocaat voor \u20ac139 \u2014 niet \u20ac250/uur'],
      mockFile: 'ingebrekestelling.pdf ge\u00fcpload',
      mockAnalyzing: 'Analyseren…',
      mockScore: '78 / 100 \u2014 Hoog Risico',
      mockAction: 'Reageer binnen 10 dagen',
      mockFindings: ['Artikel 3.2 juridisch afdwingbaar', 'Financi\u00eble blootstelling tot \u20ac8.400', 'Sterke onderhandelingspositie'],
    },
    howItWorks: {
      label: 'Hoe het werkt',
      h2: 'Van verward naar zelfverzekerd \u2014 in 4 stappen',
      steps: [
        { title: 'Upload uw document', desc: 'Elk formaat. Ingebrekestelling, contract, dagvaarding. 10 seconden.' },
        { title: 'Krijg uw Risicoscore', desc: 'Score van 0\u2013100. In het Nederlands. Zonder jargon. In enkele minuten.' },
        { title: 'Uw dossier blijft actief', desc: 'Voeg documenten toe. Score wordt bijgewerkt. Volgende stap altijd duidelijk.' },
        { title: 'Spreek met een advocaat', desc: '\u20ac139 voor 30 min. Advocaat al gebrieft. Geen tijdverlies.' },
      ],
    },
    lawyersCta: {
      badge: 'Advocaten nu beschikbaar',
      h2: ['Spreek met een echte advocaat.', 'Via video. Nu meteen.'],
      p: 'Wacht niet dagen op een afspraak. Verbind met een erkende Belgische advocaat in minuten \u2014 al gebrieft over uw dossier. 30 minuten, vast tarief, geen verrassingen.',
      cta: 'Boek een videogesprek \u2014 \u20ac139',
      footnote: 'Dezelfde dag beschikbaar \u00b7 Heel Belgi\u00eb \u00b7 Erkende advocaten',
      availableNow: 'Nu beschikbaar',
      availableIn: 'Beschikbaar in',
    },
    lawyersSection: { label: 'Onze advocaten', h2: 'Echte advocaten. Geverifieerd. Klaar.' },
    pricing: {
      label: 'Prijzen',
      h2: 'Hulp op advocaatniveau. Niet de prijs van een advocaat.',
      free: { name: 'Gratis', price: '\u20ac0', period: 'voor altijd', cta: 'Gratis beginnen', features: ['1 gratis documentanalyse', 'Overzicht Risicoscore'], disabled: ['Volledig rapport', 'Live dossier', 'Toegang advocaat'] },
      pro: { name: 'Pro', price: '\u20ac65', period: 'per maand', cta: 'Beginnen', badge: 'Meest populair', features: ['Onbeperkte analyses', 'Volledige rapporten', 'Live dossier', 'Dynamische Risicoscore', 'Prioritaire support'] },
      attorney: { name: 'Advocaat gesprek', price: '\u20ac139', period: 'per sessie', cta: 'Boeken', features: ['30 min videogesprek', 'Erkende Belgische advocaat', 'Gebrieft door Archer AI', 'Schriftelijke opvolging', 'Dezelfde dag beschikbaar'] },
    },
    reviews: {
      label: 'Beoordelingen',
      h2: 'Echte mensen. Echte resultaten.',
      items: [
        { text: '"Ik kreeg een ingebrekestelling en raakte in paniek. Archer vertelde me dat mijn risico 78/100 was en precies wat ik moest doen. Het gesprek met de advocaat redde me van een grote fout."', name: 'Jan V.', loc: 'Antwerpen, Belgi\u00eb' },
        { text: '"De advocaat kende al mijn hele dossier voor we spraken. Beste \u20ac139 die ik ooit heb uitgegeven. Bij een gewoon kantoor had ik \u20ac1.500 betaald."', name: 'Lies D.', loc: 'Gent, Belgi\u00eb' },
        { text: '"Mijn verhuurder stuurde een illegaal uitzettingsbericht. Archer herkende het meteen. Mijn score daalde van 71 naar 14 na het versturen van het antwoord."', name: 'Pieter M.', loc: 'Leuven, Belgi\u00eb' },
      ],
    },
    faq: {
      label: 'FAQ',
      h2: 'Veelgestelde vragen',
      items: [
        { q: 'Geeft Archer echt juridisch advies?', a: 'Archer biedt juridische informatie en documentanalyse \u2014 geen juridisch advies. Wanneer u echt advies nodig heeft, verbindt Archer u met een erkende advocaat.' },
        { q: 'Is mijn document veilig?', a: 'Alle documenten zijn versleuteld met 256-bit encryptie. We delen uw documenten nooit met derden. U kunt uw gegevens op elk moment verwijderen.' },
        { q: 'Wie zijn de advocaten op Archer?', a: 'Alle advocaten zijn ingeschreven bij de Belgische Balie, met minimaal 5 jaar ervaring, en doorlopen een streng verificatieproces.' },
        { q: 'Begrijpt Archer Belgisch recht?', a: 'Ja. Archer is getraind op het Belgisch Burgerlijk Wetboek, het Gerechtelijk Wetboek, Belgisch arbeidsrecht, regionale regelgeving en Belgische rechtspraak.' },
        { q: 'Hoe nauwkeurig is de Risicoscore?', a: 'De Risicoscore wordt gegenereerd door AI getraind op duizenden juridische documenten en gevalideerd door advocaten. Het is een ori\u00ebntatietool.' },
        { q: 'Kan ik mijn abonnement op elk moment opzeggen?', a: 'Ja. Opzeggen op elk moment met \u00e9\u00e9n klik. Geen vragen, geen opzegkosten. Uw dossiers blijven toegankelijk tot het einde van uw factureringsperiode.' },
      ],
    },
    finalCta: {
      h2: ['Uw advocaat, zonder', 'de advocaatkosten.'],
      p: 'Sluit u aan bij 4.200+ Belgen die Archer gebruiken om zichzelf te beschermen \u2014 voor minder.',
      cta: 'Analyseer mijn document \u2014 het is gratis',
    },
    footer: 'Archer biedt juridische informatie en documentanalyse, geen juridisch advies. Advocaatconsultaties worden uitgevoerd door onafhankelijke, erkende juridische professionals. Archer cre\u00ebert geen advocaat-cli\u00ebntrelatie.',
    lawyers: [
      { name: 'Pieter Van den Berg', specialty: 'Arbeidsrecht', bar: 'Balie Gent', years: 9, tags: ['Ontslag', 'Discriminatie'], rating: 5.0, sessions: 145, status: 'now', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop' },
      { name: 'Laura Janssen', specialty: 'Huurrecht', bar: 'Balie Antwerpen', years: 7, tags: ['Huurgeschillen', 'Uitzetting'], rating: 4.9, sessions: 112, status: '8 min', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop' },
      { name: 'Sarah De Smedt', specialty: 'Familierecht', bar: 'Balie Leuven', years: 11, tags: ['Echtscheiding', 'Voogdij'], rating: 4.9, sessions: 178, status: '15 min', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop' },
    ],
  },

  'be-de': {
    locale: 'be-de',
    country: 'BE',
    flag: '\u{1F1E7}\u{1F1EA}',
    countryName: 'Belgium',
    langName: 'Deutsch',
    currency: { symbol: '\u20ac', code: 'EUR' },
    nav: { howItWorks: 'So funktioniert es', attorneys: 'Anw\u00e4lte', engine: 'Engine', pricing: 'Preise', faq: 'FAQ', signIn: 'Anmelden', getStarted: 'Loslegen' },
    hero: {
      badge: 'Von 4.200+ Belgiern genutzt',
      h1: ['Ihr Anwalt,', 'ohne die', 'Anwaltskosten.'],
      sub: 'Ein rechtliches Problem? Laden Sie Ihr Dokument hoch. Archer analysiert Ihr Risiko in wenigen Minuten, sagt Ihnen <strong>genau was zu tun ist</strong> \u2014 und verbindet Sie mit einem echten Anwalt nur wenn n\u00f6tig.',
      cta: 'Mein Dokument analysieren \u2014 kostenlos',
      ctaLawyer: 'Jetzt mit einem Anwalt sprechen',
      footnote: 'Keine Kreditkarte \u00b7 Wenige Minuten \u00b7 Zugelassene belgische Anw\u00e4lte',
      riskLabel: 'Ihr Risikoscore',
      riskAction: 'Innerhalb von 7 Tagen handeln',
      availableNow: '3 Anw\u00e4lte verf\u00fcgbar',
    },
    animated: {
      label: 'Archer in Aktion',
      h2: ['Egal was Ihre Situation ist \u2014', 'Archer k\u00fcmmert sich bereits darum.'],
      sub: 'Jeder Fall verfolgt. Jede Frist sichtbar. Jedes Risiko sofort bewertet.',
      aiBar: 'Archer AI \u2014 <span class="text-[#1a56db] font-medium">78% der F\u00e4lle durch KI allein gel\u00f6st \u2014 kein Anwalt n\u00f6tig.</span>',
    },
    stats: {
      docs: '11.832', docsLabel: 'Dokumente analysiert',
      users: '4.217', usersLabel: 'Belgier gesch\u00fctzt',
      saved: '\u20ac2,1M', savedLabel: 'Rechtskosten gespart',
      rating: '4.9/5', ratingLabel: 'Durchschnittsbewertung',
    },
    trust: ['Zugelassene belgische Anw\u00e4lte', 'Ergebnisse in wenigen Minuten', '256-Bit-Verschl\u00fcsselung', 'Von Anw\u00e4lten validierte KI', 'Keine versteckten Kosten'],
    press: 'Bekannt aus',
    categories: {
      sectionLabel: 'Was Archer behandelt',
      h2: 'Egal was Ihr rechtliches Problem ist \u2014 Archer l\u00f6st es.',
      sub: 'Von Abmahnungen bis Arbeitsstreitigkeiten, von Mietproblemen bis Handelsvertr\u00e4gen \u2014 Archer deckt alle h\u00e4ufigen Rechtssituationen in Belgien ab.',
      items: [
        { title: 'Abmahnungen', desc: 'Einen drohenden Rechtsbrief erhalten? Kennen Sie Ihre Rechte und wie Sie reagieren k\u00f6nnen.', badge: 'H\u00e4ufig', badgeClass: 'badge-blue' },
        { title: 'Arbeitsrecht', desc: 'Unrechtm\u00e4\u00dfige K\u00fcndigung, unbezahlte L\u00f6hne, Diskriminierung, Wettbewerbsverbot.', badge: 'Hohes Risiko', badgeClass: 'badge-red' },
        { title: 'Miet- & Mieterrechte', desc: 'Illegale R\u00e4umung, Kautionsstreitigkeiten, unfaire Klauseln, Vermieterpflichten.', badge: 'Sehr h\u00e4ufig', badgeClass: 'badge-green' },
        { title: 'Handelsvertr\u00e4ge', desc: 'NDA, Partnerschaftsvereinbarungen, Dienstleistungsvertr\u00e4ge, Lieferantenstreitigkeiten.', badge: 'B2B', badgeClass: 'badge-blue' },
        { title: 'Inkasso', desc: 'Vom Gerichtsvollzieher bel\u00e4stigt? Kennen Sie Ihre Rechte und stoppen Sie die Verfahren.', badge: 'Dringend', badgeClass: 'badge-red' },
        { title: 'Verbraucherschutz', desc: 'Betrug, R\u00fcckerstattungsstreitigkeiten, Garantiean-spr\u00fcche, unfaire Gesch\u00e4ftspraktiken.', badge: 'H\u00e4ufig', badgeClass: 'badge-green' },
        { title: 'Familienrecht', desc: 'Scheidung, Sorgerecht, Unterhalt, Verm\u00f6gensaufteilung.', badge: 'Sensibel', badgeClass: 'badge-gray' },
        { title: 'Immobilienrecht', desc: 'Kaufvertrag, versteckte M\u00e4ngel, Miteigentum, Nachbarschaftsstreitigkeiten.', badge: 'Hoher Wert', badgeClass: 'badge-red' },
        { title: 'Verwaltungsrecht', desc: 'Bu\u00dfgelder, Baugenehmigungen, Streitigkeiten mit der Gemeinde.', badge: 'Komplex', badgeClass: 'badge-blue' },
      ],
    },
    problem: {
      label: 'Das Problem',
      h2: 'Rechtshilfe kostete \u20ac300 nur um eine Frage zu stellen.',
      p: 'Sie haben eine Abmahnung erhalten. Einen unverst\u00e4ndlichen Vertrag. Eine R\u00e4umungsankündigung. Sie sind gestresst \u2014 und ein echter Anwalt kostet \u20ac250/Stunde. Archer \u00e4ndert das.',
      steps: ['Laden Sie Ihr Dokument in Sekunden hoch', 'Erhalten Sie Ihren Risikoscore sofort', 'Wissen Sie genau was zu tun ist', 'Sprechen Sie mit einem echten Anwalt f\u00fcr \u20ac139 \u2014 nicht \u20ac250/Std.'],
      mockFile: 'abmahnung.pdf hochgeladen',
      mockAnalyzing: 'Analyse läuft…',
      mockScore: '78 / 100 \u2014 Hohes Risiko',
      mockAction: 'Innerhalb von 10 Tagen reagieren',
      mockFindings: ['Klausel 3.2 rechtlich durchsetzbar', 'Finanzielle Belastung bis zu \u20ac8.400', 'Starke Verhandlungsposition'],
    },
    howItWorks: {
      label: 'So funktioniert es',
      h2: 'Von verwirrt zu selbstsicher \u2014 in 4 Schritten',
      steps: [
        { title: 'Laden Sie Ihr Dokument hoch', desc: 'Jedes Format. Abmahnung, Vertrag, Vorladung. 10 Sekunden.' },
        { title: 'Erhalten Sie Ihren Risikoscore', desc: 'Score von 0\u2013100. Auf Deutsch. Ohne Fachjargon. In wenigen Minuten.' },
        { title: 'Ihr Fall bleibt aktiv', desc: 'F\u00fcgen Sie Dokumente hinzu. Score wird aktualisiert. N\u00e4chster Schritt immer klar.' },
        { title: 'Sprechen Sie mit einem Anwalt', desc: '\u20ac139 f\u00fcr 30 Min. Anwalt bereits gebrieft. Keine Zeitverschwendung.' },
      ],
    },
    lawyersCta: {
      badge: 'Anw\u00e4lte jetzt verf\u00fcgbar',
      h2: ['Sprechen Sie mit einem echten Anwalt.', 'Per Video. Jetzt sofort.'],
      p: 'Warten Sie nicht tagelang auf einen Termin. Verbinden Sie sich in Minuten mit einem zugelassenen belgischen Anwalt \u2014 bereits \u00fcber Ihren Fall gebrieft. 30 Minuten, Festpreis, keine \u00dcberraschungen.',
      cta: 'Videoanruf buchen \u2014 \u20ac139',
      footnote: 'Am selben Tag verf\u00fcgbar \u00b7 Ganz Belgien \u00b7 Zugelassene Anw\u00e4lte',
      availableNow: 'Jetzt verf\u00fcgbar',
      availableIn: 'Verf\u00fcgbar in',
    },
    lawyersSection: { label: 'Unsere Anw\u00e4lte', h2: 'Echte Anw\u00e4lte. Gepr\u00fcft. Bereit.' },
    pricing: {
      label: 'Preise',
      h2: 'Hilfe auf Anwaltsniveau. Nicht der Preis eines Anwalts.',
      free: { name: 'Kostenlos', price: '\u20ac0', period: 'f\u00fcr immer', cta: 'Kostenlos starten', features: ['1 kostenlose Dokumentenanalyse', '\u00dcbersicht Risikoscore'], disabled: ['Vollst\u00e4ndiger Bericht', 'Live-Akte', 'Anwaltszugang'] },
      pro: { name: 'Pro', price: '\u20ac65', period: 'pro Monat', cta: 'Loslegen', badge: 'Am beliebtesten', features: ['Unbegrenzte Analysen', 'Vollst\u00e4ndige Berichte', 'Live-Akte', 'Dynamischer Risikoscore', 'Priorit\u00e4tssupport'] },
      attorney: { name: 'Anwaltsgespr\u00e4ch', price: '\u20ac139', period: 'pro Sitzung', cta: 'Buchen', features: ['30-Min-Videoanruf', 'Zugelassener belgischer Anwalt', 'Von Archer AI gebrieft', 'Schriftliche Nachverfolgung', 'Am selben Tag verf\u00fcgbar'] },
    },
    reviews: {
      label: 'Bewertungen',
      h2: 'Echte Menschen. Echte Ergebnisse.',
      items: [
        { text: '"Ich bekam eine Abmahnung und geriet in Panik. Archer sagte mir, dass mein Risiko 78/100 betrug und genau was ich tun sollte. Das Gespr\u00e4ch mit dem Anwalt rettete mich vor einem gro\u00dfen Fehler."', name: 'Klaus M.', loc: 'Eupen, Belgien' },
        { text: '"Der Anwalt kannte meinen gesamten Fall bevor wir sprachen. Die besten \u20ac139 die ich je ausgegeben habe. Bei einer normalen Kanzlei h\u00e4tte ich \u20ac1.500 gezahlt."', name: 'Anna S.', loc: 'Malmedy, Belgien' },
        { text: '"Mein Vermieter schickte eine illegale R\u00e4umungsanzeige. Archer erkannte es sofort. Mein Score sank von 71 auf 14 nachdem ich die Antwort geschickt habe."', name: 'Peter K.', loc: 'Sankt Vith, Belgien' },
      ],
    },
    faq: {
      label: 'FAQ',
      h2: 'H\u00e4ufig gestellte Fragen',
      items: [
        { q: 'Gibt Archer echte Rechtsberatung?', a: 'Archer bietet rechtliche Informationen und Dokumentenanalyse \u2014 keine Rechtsberatung. Wenn Sie echte Beratung ben\u00f6tigen, verbindet Archer Sie mit einem zugelassenen Anwalt.' },
        { q: 'Ist mein Dokument sicher?', a: 'Alle Dokumente sind mit 256-Bit-Verschl\u00fcsselung verschl\u00fcsselt. Wir teilen Ihre Dokumente niemals mit Dritten. Sie k\u00f6nnen Ihre Daten jederzeit l\u00f6schen.' },
        { q: 'Wer sind die Anw\u00e4lte bei Archer?', a: 'Alle Anw\u00e4lte sind bei der belgischen Anwaltskammer zugelassen, mit mindestens 5 Jahren Erfahrung und einem strengen Pr\u00fcfungsverfahren.' },
        { q: 'Versteht Archer belgisches Recht?', a: 'Ja. Archer ist auf das belgische Zivilgesetzbuch, das Gerichtsgesetzbuch, belgisches Arbeitsrecht, regionale Vorschriften und belgische Rechtsprechung geschult.' },
        { q: 'Wie genau ist der Risikoscore?', a: 'Der Risikoscore wird von einer KI generiert, die auf Tausenden von Rechtsdokumenten trainiert und von Anw\u00e4lten validiert wurde. Es ist ein Orientierungswerkzeug.' },
        { q: 'Kann ich mein Abonnement jederzeit k\u00fcndigen?', a: 'Ja. K\u00fcndigen Sie jederzeit mit einem Klick. Keine Fragen, keine K\u00fcndigungsgeb\u00fchren. Ihre Akten bleiben bis zum Ende Ihres Abrechnungszeitraums zug\u00e4nglich.' },
      ],
    },
    finalCta: {
      h2: ['Ihr Anwalt, ohne', 'die Anwaltskosten.'],
      p: 'Schlie\u00dfen Sie sich 4.200+ Belgiern an, die Archer nutzen um sich zu sch\u00fctzen \u2014 f\u00fcr weniger.',
      cta: 'Mein Dokument analysieren \u2014 kostenlos',
    },
    footer: 'Archer bietet rechtliche Informationen und Dokumentenanalyse, keine Rechtsberatung. Anwaltsberatungen werden von unabh\u00e4ngigen, zugelassenen Rechtsexperten durchgef\u00fchrt. Archer begr\u00fcndet kein Anwalts-Mandanten-Verh\u00e4ltnis.',
    lawyers: [
      { name: 'Klaus Mueller', specialty: 'Arbeitsrecht', bar: 'Anwaltskammer Eupen', years: 10, tags: ['K\u00fcndigung', 'Diskriminierung'], rating: 5.0, sessions: 98, status: 'now', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop' },
      { name: 'Anna Schreiber', specialty: 'Familienrecht', bar: 'Anwaltskammer Malmedy', years: 8, tags: ['Scheidung', 'Sorgerecht'], rating: 4.9, sessions: 76, status: '15 min', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop' },
      { name: 'Sophie Lecomte', specialty: 'Vertragsrecht', bar: 'Barreau de Bruxelles', years: 10, tags: ['Vertr\u00e4ge', 'Handelsrecht'], rating: 4.9, sessions: 167, status: '22 min', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop' },
    ],
  },

  'us-es': {
    locale: 'us-es',
    country: 'US',
    flag: '\u{1F1FA}\u{1F1F8}',
    countryName: 'Estados Unidos',
    langName: 'Espa\u00f1ol',
    currency: { symbol: '$', code: 'USD' },
    nav: { howItWorks: 'Como funciona', attorneys: 'Abogados', engine: 'Motor', pricing: 'Precios', faq: 'Preguntas', signIn: 'Iniciar sesion', getStarted: 'Comenzar' },
    hero: {
      badge: 'Confiado por mas de 12,000 estadounidenses',
      h1: ['Tu abogado,', 'sin los', 'honorarios.'],
      sub: 'Tienes un problema legal? Sube tu documento. Archer analiza tu riesgo en unos minutos, te dice <strong>exactamente que hacer</strong>, y te conecta con un abogado real solo cuando lo necesitas.',
      cta: 'Analizar mi documento · gratis',
      ctaLawyer: 'Hablar con un abogado ahora',
      footnote: 'Sin tarjeta de credito \u00b7 Unos minutos \u00b7 Abogados licenciados disponibles',
      riskLabel: 'Tu puntuacion de riesgo',
      riskAction: 'Actuar en 7 dias',
      availableNow: '3 abogados disponibles ahora',
    },
    animated: {
      label: 'Archer en accion',
      h2: ['Sea cual sea tu situacion,', 'Archer ya esta trabajando.'],
      sub: 'Cada caso rastreado. Cada fecha limite visible. Cada riesgo evaluado al instante.',
      aiBar: 'Archer AI · <span class="text-[#1a56db] font-medium">78% de los casos resueltos por IA, sin abogado.</span>',
    },
    stats: {
      docs: '12,847', docsLabel: 'Documentos analizados',
      users: '4,392', usersLabel: 'Estadounidenses protegidos',
      saved: '$8.2M', savedLabel: 'Honorarios legales ahorrados',
      rating: '4.9/5', ratingLabel: 'Calificacion promedio',
    },
    trust: ['Abogados licenciados en EE.UU.', 'Resultados en unos minutos', 'Cifrado 256-bit', 'IA revisada por abogados', 'Sin costos ocultos'],
    press: 'Visto en',
    categories: {
      sectionLabel: 'Lo que Archer maneja',
      h2: 'Sea cual sea tu problema legal, Archer lo maneja.',
      sub: 'Desde contratos laborales hasta disputas de alquiler, deudas hasta divorcios, Archer cubre cada situacion legal comun que enfrentan los estadounidenses.',
      items: [
        { title: 'Contratos laborales', desc: 'Despido injustificado, clausulas de no competencia, acoso laboral, EEOC.', badge: 'Mas comun', badgeClass: 'badge-blue' },
        { title: 'Disputas de vivienda', desc: 'Desalojos, depositos, condiciones de habitabilidad, derechos del inquilino.', badge: 'Muy comun', badgeClass: 'badge-green' },
        { title: 'Cobro de deudas', desc: 'Cartas de cobro, verificacion FDCPA, negociacion, proteccion contra acoso.', badge: 'Urgente', badgeClass: 'badge-red' },
        { title: 'Derecho familiar', desc: 'Divorcio, custodia, manutencion, ordenes de proteccion.', badge: 'Sensible', badgeClass: 'badge-gray' },
        { title: 'Proteccion al consumidor', desc: 'Productos defectuosos, disputas de servicios, reembolsos.', badge: 'Comun', badgeClass: 'badge-green' },
        { title: 'Lesiones personales', desc: 'Accidentes, negligencia medica, compensacion laboral.', badge: 'Alto valor', badgeClass: 'badge-red' },
        { title: 'Contratos y acuerdos', desc: 'Revision de NDA, contratos comerciales, acuerdos de arrendamiento.', badge: 'Negocios', badgeClass: 'badge-blue' },
        { title: 'Delitos menores', desc: 'Infracciones de trafico, cargos menores, antecedentes penales.', badge: 'Urgente', badgeClass: 'badge-red' },
        { title: 'Inmigracion', desc: 'Visas de trabajo, residencia, peticiones familiares, DACA.', badge: 'Especializado', badgeClass: 'badge-blue' },
      ],
    },
    problem: {
      label: 'El problema',
      h2: 'La ayuda legal costaba $350 solo por hacer una pregunta.',
      p: 'Recibiste una carta de cobro. Un contrato que no entiendes. Un aviso de desalojo. Estas estresado, y un abogado real cuesta $350/hora solo por contestar el telefono. Archer cambia eso.',
      steps: ['Sube tu documento en segundos', 'Obtiene tu puntuacion de riesgo al instante', 'Sabe exactamente que hacer', 'Habla con un abogado real por $149, no $350/hora'],
      mockFile: 'demand_letter.pdf subido',
      mockAnalyzing: 'Analizando…',
      mockScore: '72 / 100 · Riesgo Alto',
      mockAction: 'Responder en 14 dias',
      mockFindings: ['Clausula 4.1 viola la FDCPA', 'Exposicion financiera hasta $12,400', 'Fuerte caso para negociacion'],
    },
    howItWorks: {
      label: 'Como funciona',
      h2: 'De confundido a seguro, en 4 pasos',
      steps: [
        { title: 'Sube tu documento', desc: 'Cualquier formato. Carta de cobro, contrato, aviso judicial. Toma 10 segundos.' },
        { title: 'Obtiene tu puntuacion de riesgo', desc: 'Puntuacion de 0-100. Sin jerga legal. En unos minutos.' },
        { title: 'Tu caso se mantiene vivo', desc: 'Agrega documentos cuando lleguen. Puntuacion actualizada. Siguiente paso siempre claro.' },
        { title: 'Habla con un abogado', desc: '$149 por 30 min. Abogado pre-informado. Sin perder tiempo.' },
      ],
    },
    lawyersCta: {
      badge: 'Abogados disponibles ahora',
      h2: ['Habla con un abogado real.', 'Por video. Ahora mismo.'],
      p: 'No esperes dias por una cita. Conecta con un abogado licenciado en EE.UU. en minutos, pre-informado con tu caso completo. 30 minutos, tarifa fija, sin sorpresas.',
      cta: 'Reservar videollamada · $149',
      footnote: 'Disponible el mismo dia \u00b7 50 estados \u00b7 Licenciados y verificados',
      availableNow: 'Disponible ahora',
      availableIn: 'Disponible en',
    },
    lawyersSection: { label: 'Nuestros abogados', h2: 'Abogados reales. Verificados. Listos.' },
    pricing: {
      label: 'Precios',
      h2: 'Ayuda nivel abogado. Sin precios de abogado.',
      free: { name: 'Gratis', price: '$0', period: 'para siempre', cta: 'Comenzar gratis', features: ['1 analisis gratuito', 'Vista general de riesgo'], disabled: ['Informe completo', 'Expediente activo', 'Acceso a abogado'] },
      pro: { name: 'Pro', price: '$69', period: 'por mes', cta: 'Comenzar', badge: 'Mas popular', features: ['Analisis ilimitados', 'Informes completos', 'Expediente activo', 'Puntuacion dinamica', 'Soporte prioritario'] },
      attorney: { name: 'Llamada con abogado', price: '$149', period: 'por sesion', cta: 'Reservar llamada', features: ['Videollamada de 30 min', 'Abogado licenciado EE.UU.', 'Pre-informado por Archer AI', 'Seguimiento escrito', 'Disponibilidad inmediata'] },
    },
    reviews: {
      label: 'Opiniones',
      h2: 'Personas reales. Resultados reales.',
      items: [
        { text: '"Mi empleador me despidio injustamente. Archer identifico 3 violaciones de la ley laboral y recupere toda mi indemnizacion. La llamada con el abogado valio cada centavo."', name: 'Carlos M.', loc: 'Miami, FL' },
        { text: '"Mi propietario intento subirme el alquiler un 40%. Archer me mostro la ley y redacto una carta. Problema resuelto en 48 horas."', name: 'Maria L.', loc: 'Houston, TX' },
        { text: '"Recibi una carta de cobranza por una deuda vieja. Archer me explico mis derechos bajo la FDCPA y me ahorro el panico."', name: 'Roberto P.', loc: 'Los Angeles, CA' },
      ],
    },
    faq: {
      label: 'Preguntas frecuentes',
      h2: 'Preguntas que probablemente tienes',
      items: [
        { q: 'Archer me da asesoria legal real?', a: 'Archer proporciona informacion legal y analisis de documentos, no asesoria legal. Cuando necesitas asesoria legal real, Archer te conecta con un abogado licenciado.' },
        { q: 'Mi documento esta seguro?', a: 'Todos los documentos se cifran con encriptacion de 256 bits. Nunca compartimos tus documentos con terceros. Puedes eliminar tus datos en cualquier momento.' },
        { q: 'Quienes son los abogados en Archer?', a: 'Todos los abogados estan licenciados en EE.UU., con un minimo de 5 anos de experiencia, y pasan por un riguroso proceso de verificacion.' },
        { q: 'Archer entiende la ley de EE.UU.?', a: 'Si. Archer esta entrenado en leyes federales y estatales de EE.UU., incluyendo FDCPA, FCRA, leyes de inquilinos, derecho laboral y mas.' },
        { q: 'Que tan precisa es la puntuacion de riesgo?', a: 'La puntuacion de riesgo es generada por IA entrenada en miles de documentos legales y validada por abogados licenciados. Es una herramienta de orientacion.' },
        { q: 'Puedo cancelar mi suscripcion?', a: 'Si. Cancela en cualquier momento con un clic. Sin preguntas, sin cargos por cancelacion.' },
      ],
    },
    finalCta: {
      h2: ['Tu abogado, sin', 'los honorarios.'],
      p: 'Unete a mas de 12,000 estadounidenses que usan Archer para protegerse, por menos.',
      cta: 'Analizar mi documento · es gratis',
    },
    footer: 'Archer proporciona informacion legal y analisis de documentos, no asesoria legal. Las consultas con abogados son realizadas por profesionales legales independientes y licenciados.',
    lawyers: [
      { name: 'Sarah Mitchell, Esq.', specialty: 'Derecho laboral', bar: 'Colegio de Abogados de NY', years: 12, tags: ['Despido', 'Discriminacion'], rating: 5.0, sessions: 234, status: 'now', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop' },
      { name: 'Archer Carter, Esq.', specialty: 'Derecho contractual', bar: 'Colegio de Abogados de CA', years: 9, tags: ['Contratos', 'NDA'], rating: 4.9, sessions: 187, status: '8 min', photo: 'https://images.unsplash.com/photo-1644268756851-3f69ffb9553f?w=200&h=200&fit=crop' },
      { name: 'Diana Torres, Esq.', specialty: 'Derechos del inquilino', bar: 'Colegio de Abogados de TX', years: 7, tags: ['Arrendamiento', 'Desalojo'], rating: 4.9, sessions: 156, status: '22 min', photo: 'https://images.unsplash.com/photo-1685760259914-ee8d2c92d2e0?w=200&h=200&fit=crop' },
    ],
  },

  'be-en': {
    locale: 'be-en',
    country: 'BE',
    flag: '\u{1F1E7}\u{1F1EA}',
    countryName: 'Belgium',
    langName: 'English',
    currency: { symbol: '\u20ac', code: 'EUR' },
    nav: { howItWorks: 'How it works', attorneys: 'Attorneys', engine: 'Engine', pricing: 'Pricing', faq: 'FAQ', signIn: 'Sign in', getStarted: 'Get started' },
    hero: {
      badge: 'Trusted by 5,000+ Belgian residents',
      h1: ['Your lawyer,', 'without the', 'lawyer fees.'],
      sub: 'Got a legal problem? Upload your document. Archer analyzes your risk in a few minutes, tells you <strong>exactly what to do</strong>, and connects you to a real attorney only when you need one.',
      cta: 'Analyze my document · free',
      ctaLawyer: 'Talk to a lawyer now',
      footnote: 'No credit card \u00b7 a few minutes \u00b7 Licensed Belgian attorneys',
      riskLabel: 'Your Risk Score',
      riskAction: 'Act within 7 days',
      availableNow: '3 attorneys available now',
    },
    animated: {
      label: 'See Archer in action',
      h2: ['Whatever your situation,', 'Archer is already on it.'],
      sub: 'Every case tracked. Every deadline visible. Every risk scored instantly.',
      aiBar: 'Archer AI · <span class="text-[#1a56db] font-medium">78% of cases resolved by AI alone, no lawyer needed.</span>',
    },
    stats: {
      docs: '6,291', docsLabel: 'Documents analyzed',
      users: '5,012', usersLabel: 'Belgian residents protected',
      saved: '\u20ac4.7M', savedLabel: 'Legal fees saved',
      rating: '4.9/5', ratingLabel: 'Average rating',
    },
    trust: ['Licensed Belgian attorneys', 'Results in a few minutes', '256-bit encryption', 'Attorney-reviewed AI', 'No hidden fees'],
    press: 'As seen in',
    categories: {
      sectionLabel: 'What Archer handles',
      h2: 'Whatever your legal problem, Archer handles it.',
      sub: 'From employment contracts to tenancy disputes, debt collection to family law, Archer covers every common legal situation Belgian residents face.',
      items: [
        { title: 'Employment contracts', desc: 'Unfair dismissal, notice periods, non-compete clauses under Belgian Labour Law.', badge: 'Most common', badgeClass: 'badge-blue' },
        { title: 'Housing disputes', desc: 'Lease termination, deposit recovery, rent regulations.', badge: 'Very common', badgeClass: 'badge-green' },
        { title: 'Debt collection', desc: 'Demand letters, payment plans, bailiff procedures.', badge: 'Urgent', badgeClass: 'badge-red' },
        { title: 'Family law', desc: 'Divorce, custody, maintenance, mediation.', badge: 'Sensitive', badgeClass: 'badge-gray' },
        { title: 'Consumer protection', desc: 'Defective goods, warranty claims, e-commerce disputes.', badge: 'Common', badgeClass: 'badge-green' },
        { title: 'Criminal matters', desc: 'Police complaints, minor offenses, know your rights.', badge: 'Time-sensitive', badgeClass: 'badge-red' },
        { title: 'Contracts', desc: 'NDA review, commercial contracts, lease agreements.', badge: 'Business', badgeClass: 'badge-blue' },
        { title: 'Administrative law', desc: 'Urban planning permits, tax disputes, social security.', badge: 'Specialized', badgeClass: 'badge-blue' },
        { title: 'Insurance disputes', desc: 'Claim denials, accident liability, coverage disputes.', badge: 'High value', badgeClass: 'badge-red' },
      ],
    },
    problem: {
      label: 'The problem',
      h2: 'Legal help used to cost \u20ac200 just to ask a question.',
      p: "You received a demand letter. A contract you don't understand. A bailiff notice. You're stressed, and a real lawyer costs \u20ac150/hr just to pick up the phone. Archer changes that.",
      steps: ['Upload your document in seconds', 'Get your Risk Score instantly', 'Know exactly what to do next', 'Talk to a real lawyer for \u20ac149, not \u20ac150/hr'],
      mockFile: 'mise_en_demeure.pdf uploaded',
      mockAnalyzing: 'Analysing…',
      mockScore: '72 / 100 · High Risk',
      mockAction: 'Respond within 15 days',
      mockFindings: ['Art. 1315 Civil Code applies', 'Financial exposure up to \u20ac4,200', 'Strong negotiation position'],
    },
    howItWorks: {
      label: 'How it works',
      h2: 'From confused to confident, in 4 steps',
      steps: [
        { title: 'Upload your document', desc: 'Any format. Demand letter, contract, court notice. Takes 10 seconds.' },
        { title: 'Get your Risk Score', desc: 'Score from 0-100. Plain language. No jargon. In a few minutes.' },
        { title: 'Your case stays alive', desc: 'Add docs as they arrive. Score updates. Next step always clear.' },
        { title: 'Talk to a lawyer', desc: '\u20ac149 for 30 min. Attorney pre-briefed. No time wasted.' },
      ],
    },
    lawyersCta: {
      badge: 'Attorneys available right now',
      h2: ['Talk to a real lawyer.', 'By video. Right now.'],
      p: "Don't wait days for an appointment. Connect to a licensed Belgian attorney in minutes, pre-briefed with your full case. 30 minutes, flat fee, no surprises.",
      cta: 'Book a video call · \u20ac149',
      footnote: 'Available same day \u00b7 All regions \u00b7 Licensed & vetted',
      availableNow: 'Available now',
      availableIn: 'Available in',
    },
    lawyersSection: { label: 'Our attorneys', h2: 'Real lawyers. Vetted. Ready.' },
    pricing: {
      label: 'Pricing',
      h2: 'Lawyer-level help. Not lawyer-level prices.',
      free: { name: 'Free', price: '\u20ac0', period: 'forever', cta: 'Start for free', features: ['1 free document analysis', 'Risk Score overview'], disabled: ['Full risk report', 'Live case file', 'Attorney access'] },
      pro: { name: 'Pro', price: '\u20ac69', period: 'per month', cta: 'Get started', badge: 'Most popular', features: ['Unlimited analyses', 'Full risk reports', 'Live case file', 'Dynamic Risk Score', 'Priority support'] },
      attorney: { name: 'Attorney call', price: '\u20ac149', period: 'per session', cta: 'Book a call', features: ['30-min video call', 'Licensed Belgian attorney', 'Pre-briefed by Archer AI', 'Written follow-up', 'Same-day availability'] },
    },
    reviews: {
      label: 'Reviews',
      h2: 'Real people. Real results.',
      items: [
        { text: '"My employer dismissed me unfairly. Archer identified 3 Labour Law violations and I got my full severance. The attorney call was invaluable."', name: 'Mark D.', loc: 'Brussels, Belgium' },
        { text: '"Landlord tried to keep my deposit. Archer showed me the law and drafted a demand letter. Got my money back in 2 weeks."', name: 'Sophie V.', loc: 'Antwerp, Belgium' },
        { text: '"Received a bailiff notice. Archer explained my options and helped me negotiate a payment plan. Crisis averted."', name: 'Thomas L.', loc: 'Ghent, Belgium' },
      ],
    },
    faq: {
      label: 'FAQ',
      h2: 'Questions you probably have',
      items: [
        { q: 'Is Archer giving me real legal advice?', a: 'Archer provides legal information and document analysis, not legal advice. When you need advice, it connects you to a licensed attorney.' },
        { q: 'Is my document secure?', a: 'All documents are encrypted with 256-bit encryption. We never share them with third parties.' },
        { q: 'Who are the attorneys?', a: 'All attorneys are licensed in Belgium, with a minimum of 5 years of experience.' },
        { q: 'Does Archer understand Belgian law?', a: 'Yes. Archer is trained on Belgian federal and regional law, jurisprudence, and CCTs.' },
        { q: 'How accurate is the Risk Score?', a: 'The Risk Score is AI-generated and validated by licensed attorneys. It is a guidance tool.' },
        { q: 'Can I cancel anytime?', a: 'Yes. Cancel anytime. No cancellation fees.' },
      ],
    },
    finalCta: {
      h2: ['Your lawyer, without', 'the lawyer fees.'],
      p: 'Join 5,000+ Belgian residents who use Archer to protect themselves.',
      cta: 'Analyze my document · it\'s free',
    },
    footer: 'Archer provides legal information and document analysis, not legal advice.',
    lawyers: [
      { name: 'Sophie Lecomte', specialty: 'Labour Law', bar: 'Brussels Bar', years: 10, tags: ['Dismissal', 'Contracts'], rating: 5.0, sessions: 167, status: 'now', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop' },
      { name: 'Jan Vermeersch', specialty: 'Housing Law', bar: 'Antwerp Bar', years: 8, tags: ['Lease', 'Eviction'], rating: 4.9, sessions: 134, status: '10 min', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop' },
      { name: 'Marc Dubois', specialty: 'Family Law', bar: 'Liege Bar', years: 14, tags: ['Divorce', 'Custody'], rating: 4.9, sessions: 201, status: 'now', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop' },
    ],
  },
};

export const LOCALE_OPTIONS = [
  { key: 'us-en', flag: '\u{1F1FA}\u{1F1F8}', label: 'United States', sublabel: 'English' },
  { key: 'us-es', flag: '\u{1F1FA}\u{1F1F8}', label: 'United States', sublabel: 'Espa\u00f1ol' },
  { key: 'be-fr', flag: '\u{1F1E7}\u{1F1EA}', label: 'Belgium', sublabel: 'Fran\u00e7ais' },
  { key: 'be-nl', flag: '\u{1F1E7}\u{1F1EA}', label: 'Belgium', sublabel: 'Nederlands' },
  { key: 'be-de', flag: '\u{1F1E7}\u{1F1EA}', label: 'Belgium', sublabel: 'Deutsch' },
  { key: 'be-en', flag: '\u{1F1E7}\u{1F1EA}', label: 'Belgium', sublabel: 'English' },
];

// Map (jurisdiction, language) -> locale key for landing page translations
export const getLocaleFromPrefs = (jurisdiction, language) => {
  const j = jurisdiction || 'US';
  const l = (language || 'en').replace(/-.*/, '');
  // Try exact match first
  const exact = `${j.toLowerCase()}-${l}`;
  if (translations[exact]) return exact;
  // Fallback: use jurisdiction default
  if (j === 'BE') return 'be-fr';
  return 'us-en';
};

export const getStoredLocale = () => {
  try {
    const stored = localStorage.getItem('archer_locale');
    if (stored && typeof stored === 'string' && translations[stored]) return stored;
  } catch (e) {
    console.error('getStoredLocale read error:', e);
  }
  return 'us-en';
};

// Événement émis à chaque changement de langue. `localStorage` ne
// prévient personne dans l'onglet qui écrit (l'event `storage` natif ne
// vise que les AUTRES onglets), donc un composant qui ne porte pas
// lui-même le sélecteur — le footer, typiquement — n'avait aucun moyen
// de se remettre à jour sans rechargement.
export const LOCALE_CHANGE_EVENT = 'archer:locale-change';

export const setStoredLocale = (key) => {
  try {
    if (key && typeof key === 'string') {
      localStorage.setItem('archer_locale', key);
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent(LOCALE_CHANGE_EVENT, { detail: key }));
      }
    }
  } catch (e) {
    console.error('setStoredLocale write error:', e);
  }
};

export default translations;
